import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useBaseWidget } from '../../hooks/useBaseWidget.jsx';
import { useDatasourceData } from '../../hooks/useDatasourceData.js';
import { IMAGE_DEFAULT_FONT, VALUE_DEFAULT_FONT, UNIT_DEFAULT_FONT, NAME_ITEM_DEFAULT_FONT } from '../../settings_sidebar/chart/ChartSettings.jsx';
import ImageAttachment from '../ImageAttachment.jsx';
import StripFormatter, { calculatePresentAttributes, FILLER_TEXT } from '../base/StripFormatter.jsx';
import { useDatasource } from '../../hooks/useDatasource.js';
import { formatWithPattern } from '../../utils/formatWithPattern.js';
import { useFormulaFields, isFormulaAccessor } from '../../hooks/useFormulaFields.js';

const ATTRIBUTES = ['image', 'attachment', 'displayedInfo', 'unit', 'name_item', 'rawDisplayedInfo'];
const toUpperGerman = (s) => {
    return s.split('ß').map(s => s.toUpperCase()).join('ß')
}

// Stable reference — an inline `|| []` fallback inside useSelector creates a new array every
// render, which never compares equal, so this useEffect's [datasources, ...] dep never settles.
const EMPTY_DATASOURCES = [];

function DataStripDatasetWidget({ widgetId }) {
    const stacking = useSelector(state => state.dashboard.widgets[widgetId].settings?.stacking) || 'horizontal';
    const { rows: filteredRows, cols } = useDatasourceData(widgetId);
    const datasources = useSelector(state => state.dashboard.widgets[widgetId].settings?.datasources) || EMPTY_DATASOURCES;
    const number_format = useSelector(state => state.dashboard.widgets[widgetId].settings?.number_format);
    const asPdf = useSelector(state => state.dashboard.asPdf);
    const imageFontSize = useSelector(state => state.dashboard.widgets[widgetId].settings?.image_fontSize);
    const valueFontSize = useSelector(state => state.dashboard.widgets[widgetId].settings?.value_fontSize);
    const unitFontSize = useSelector(state => state.dashboard.widgets[widgetId].settings?.unit_fontSize);
    const nameItemFontSize = useSelector(state => state.dashboard.widgets[widgetId].settings?.name_item_fontSize);
    const [localDatasources, setLocalDatasources] = useState([]);
    const [presentAttributes, setPresentAttributes] = useState({});
    useDatasource({ widgetId, live: true });
    const { formatNumber } = useBaseWidget();
    const { applyFormulas } = useFormulaFields(widgetId);

    // `row` is the (aggregated) first datasource row, extended with the widget's formula values
    const renderDisplayedInfo = (datasource, row) => {
        let datasourceData = row[datasource.colId]
        const dataType = cols.find((c) => c.accessor === datasource.colId)

        if (Array.isArray(datasourceData)){
            datasourceData = datasourceData.flat()[0]
        }
        // A null formula result (e.g. division by zero) must not fall back to the manual quantity
        let number = isFormulaAccessor(datasource.colId) ? datasourceData : (datasourceData || datasource.quantity)
        // when number = 0 || null will return null. Below fix that
        if (datasourceData == false){
            number = datasourceData
        }
        const renderWithRollingNumbersAnimation = !asPdf
        switch (dataType) {
        case "boolean":
            number = parseBoolean({value: number})
            break;
        case "date":
            number = parseDate({value: number})
            // because parseFloat works for some date format (even though it shouldn't), so return here
            return {
                raw: number,
                formatted: number
            }
        }
        if (isNaN(parseFloat(number))) {
            return { raw: number, formatted: number }
        }
        return {
            raw: number,
            formatted: formatWithPattern(parseFloat(number), datasource.formatter) ??
                formatNumber(
                    number,
                    datasource.number_unit,
                    datasource.number_precision,
                    number_format,
                    renderWithRollingNumbersAnimation
                )
        }
    }

    useEffect(() => {
        if (datasources.length > 0){
            const firstRow = filteredRows?.[0] ?? {};
            const row = { ...firstRow, ...applyFormulas(firstRow) };
            const newSources = datasources.map(d => {
                const displayedInfo = renderDisplayedInfo(d, row)
                return ({
                    ...d,
                    displayedInfo: displayedInfo.formatted,
                    rawDisplayedInfo: displayedInfo.raw,
                    name_item: toUpperGerman(d.name_item)
                })
            })
            setLocalDatasources(newSources);
            calculatePresentAttributes(ATTRIBUTES, newSources, setPresentAttributes, ['name_item'], ['unit'])
        }
    }, [datasources, filteredRows, cols, applyFormulas])

    return (
            <div style={{height: '100%', width: '100%'}}>
                <div
                    className="section-counts"
                    style={{
                        height: '100%',
                        display: stacking === 'horizontal' ? 'grid' : 'flex',
                        flexDirection: 'column',
                        gridTemplateColumns: `repeat(${localDatasources.length}, ${100/localDatasources.length}%)`
                    }}
                >
                    {
                        localDatasources.map((d, index) => 
                            <div
                            key={index}
                            className='item flex-1'
                            style={{
                                backgroundColor: d.background_color,
                                color: d.text_color ? d.text_color : "#ffffff",
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                overflow: 'hidden'
                            }}
                            >
                                {
                                    stacking === 'horizontal' ? 
                                    <>
                                        {(Object.keys(presentAttributes).includes('image') || Object.keys(presentAttributes).includes('attachment')) &&
                                        d.image ? <p 
                                            className={"text-center widget-icon adalicon icon-" + (d.image || 'tools')}
                                            style={{"--font-size": `${imageFontSize || IMAGE_DEFAULT_FONT}px`, marginTop: '1rem', color: d.image ? d.text_color : d.background_color}}
                                            >
                                        </p>
                                        : d.attachment?.path &&
                                            <p style={{display: 'flex', justifyContent: 'center', "--font-size": `${imageFontSize || IMAGE_DEFAULT_FONT}px`}} className='widget-icon'>
                                                <ImageAttachment path={d.attachment?.path} />
                                            </p>
                                        }
                                        {Object.keys(presentAttributes).includes('displayedInfo') &&
                                         <StripFormatter
                                            presentAttributes={presentAttributes}
                                            classNames="qty numscroller"
                                            fontVar={valueFontSize}
                                            str={d.displayedInfo || FILLER_TEXT}
                                            color={d.background_color}
                                            defaultFont={VALUE_DEFAULT_FONT}
                                            attrKey='displayedInfo'
                                        />}
                                        {Object.keys(presentAttributes).includes('unit') &&
                                        <StripFormatter
                                            presentAttributes={presentAttributes}
                                            classNames='unit'
                                            fontVar={unitFontSize}
                                            str={d.unit}
                                            color={d.background_color}
                                            defaultFont={UNIT_DEFAULT_FONT}
                                            attrKey='unit'
                                        />
                                        }
                                        {Object.keys(presentAttributes).includes('name_item') &&
                                        <StripFormatter
                                            presentAttributes={presentAttributes}
                                            classNames='name'
                                            fontVar={nameItemFontSize}
                                            str={d.name_item}
                                            color={d.background_color}
                                            defaultFont={NAME_ITEM_DEFAULT_FONT}
                                            attrKey='name_item'
                                        />
                                        }
                                    </>
                                    :
                                    <>
                                        {d.image ? <p className={"text-center widget-icon adalicon icon-" + d.image} style={{"--font-size": `${imageFontSize || IMAGE_DEFAULT_FONT}px`}}></p>
                                            : d.attachment?.path && <p style={{display: 'flex', justifyContent: 'center', "--font-size": `${imageFontSize || IMAGE_DEFAULT_FONT}px`}} className='widget-icon'>
                                                <ImageAttachment path={d.attachment.path}/>
                                            </p>
                                        }
                                        {d.displayedInfo && <p className="qty numscroller mt-0" style={{"--font-size": `${valueFontSize || VALUE_DEFAULT_FONT}px`}}>
                                            {d.displayedInfo}
                                        </p>}
                                       {d.unit && <p className="unit mt-0" style={{"--font-size": `${unitFontSize || UNIT_DEFAULT_FONT}px`}}>{d.unit}</p>}
                                        {d.name_item && <p className="name mt-0" style={{"--font-size": `${nameItemFontSize || NAME_ITEM_DEFAULT_FONT}px`}}>{toUpperGerman(d.name_item)}</p>}
                                    </>
                                }
                            </div>
                        )
                    }
                </div>
            </div>
        )
    ;
}

export default DataStripDatasetWidget;
